import { useEffect, useRef, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import type { RouterOutputs } from '@/types/trpc'
import { Loader2, RotateCw, AlertTriangle } from 'lucide-react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@deepguide-ai/ui/alert-dialog'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
} from '@deepguide-ai/ui/dialog'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@deepguide-ai/ui/resizable'
import { ClipboardEnabledIframe } from '@/components/session/ClipboardEnabledIframe'

type BrowserProfile = RouterOutputs['browserProfile']['list'][number]
interface BrowserProfileDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  profile: BrowserProfile | null
  sessionUrl: string | null
  sessionLoading: boolean
  sessionId: string | null
  onLogInAgain?: () => void
  isLoggingInAgain?: boolean
  onReleaseSession?: () => Promise<void> | void
  onLoginCompletionChoice?: (
    hasLoginInfo: boolean,
    sessionId: string | null
  ) => Promise<void> | void
  isSettingLoginCompletion?: boolean
}

const TOP_HEIGHT_PX = 170
const getTopPercent = () => {
  if (typeof window === 'undefined') return 20
  return Math.min((TOP_HEIGHT_PX / window.innerHeight) * 100, 100)
}

export function BrowserProfileDialog({
  open,
  onOpenChange,
  profile,
  sessionUrl,
  sessionLoading,
  sessionId,
  onLogInAgain,
  isLoggingInAgain,
  onReleaseSession,
  onLoginCompletionChoice,
  isSettingLoginCompletion,
}: BrowserProfileDialogProps) {
  const [reloadKey, setReloadKey] = useState(0)
  const [reloading, setReloading] = useState(false)
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false)
  const [confirmingClose, setConfirmingClose] = useState(false)
  const [restartConfirmOpen, setRestartConfirmOpen] = useState(false)
  const iframeContainerRef = useRef<HTMLDivElement>(null)
  // Top panel size
  const [topSize, setTopSize] = useState(() => getTopPercent())

  // Resize listener for top panel
  useEffect(() => {
    const onResize = () => setTopSize(getTopPercent())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Warn user before page refresh/unload when dialog is open
  useEffect(() => {
    if (!open) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // Modern browsers ignore custom messages, but setting returnValue triggers the dialog
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [open])

  // Reset all dialog state when closed to prevent stale state issues
  useEffect(() => {
    if (!open) {
      // Use setTimeout to ensure cleanup happens after any pending state updates
      const timer = setTimeout(() => {
        setCloseConfirmOpen(false)
        setConfirmingClose(false)
        setRestartConfirmOpen(false)
        setReloadKey(0)
        setReloading(false)
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [open])

  const handleClose = async () => {
    try {
      if (onReleaseSession) {
        await onReleaseSession()
      }
    } catch (error) {
      console.error('Failed to release session on close:', error)
    } finally {
      onOpenChange(false)
    }
  }

  const handleDialogOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      onOpenChange(true)
      return
    }

    // Only show close confirmation if no other dialog is open
    if (!restartConfirmOpen) {
      setCloseConfirmOpen(true)
    }
  }

  const handleReload = () => {
    if (!sessionUrl) return
    setReloading(true)
    setReloadKey((prev) => prev + 1)
    setTimeout(() => setReloading(false), 300)
  }

  const handleConfirmClose = async (hasLoginInfo: boolean) => {
    setConfirmingClose(true)
    try {
      if (onLoginCompletionChoice) {
        // Pass sessionId so parent can save the session if needed
        await onLoginCompletionChoice(hasLoginInfo, sessionId)
      }
      // Success - close confirmation dialog first, then main dialog
      setConfirmingClose(false)
      setCloseConfirmOpen(false)

      // Small delay to ensure confirmation dialog is fully closed before closing main dialog
      await new Promise((resolve) => setTimeout(resolve, 100))
      await handleClose()
    } catch (error) {
      console.error('Failed to record login completion choice:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred'
      toast.error(`Failed to save session: ${errorMessage}`)
      setConfirmingClose(false)
      // Don't close the dialog on error - user can retry
    }
  }

  const isCloseActionPending =
    confirmingClose || Boolean(isSettingLoginCompletion)

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange} modal>
        <DialogContent
          closeClassName='hidden'
          className='flex h-screen max-h-screen w-screen !max-w-screen gap-0 rounded-none border-none p-0'
          onEscapeKeyDown={(e) => {
            e.preventDefault()
            handleDialogOpenChange(false)
          }}
          onPointerDownOutside={(e) => {
            e.preventDefault()
            handleDialogOpenChange(false)
          }}
        >
          <ResizablePanelGroup direction='horizontal' className='max-w-screen'>
            <ResizablePanel defaultSize='25%' minSize='20%'>
              <div className='w-full overflow-y-auto'>
                <div className='p-4'>
                  <h1 className='mb-2 text-2xl font-bold'>{profile?.name}</h1>
                  <div className='space-y-2'>
                    <p className='text-muted-foreground text-sm'>
                      <span className='text-foreground mr-2 font-medium'>
                        Session:
                      </span>
                      {profile?.isValid ? (
                        <Badge status='green'>Valid</Badge>
                      ) : (
                        <Badge status='red'>Logged out</Badge>
                      )}
                    </p>
                    <p className='text-muted-foreground text-sm'>
                      <span className='text-foreground mr-2 font-medium'>
                        Last Successful Login:
                      </span>
                      {profile?.lastValidatedAt
                        ? formatDistanceToNow(
                            new Date(profile.lastValidatedAt),
                            {
                              addSuffix: true,
                            }
                          )
                        : 'Never'}
                    </p>
                    <p className='text-muted-foreground text-sm'>
                      <span className='text-foreground mr-2 font-medium'>
                        Role:
                      </span>
                      {profile?.profileType || '—'}
                    </p>
                    <p className='text-muted-foreground text-sm'>
                      <span className='text-foreground mr-2 font-medium'>
                        Email:
                      </span>
                      {profile?.email || '—'}
                    </p>

                    <p className='text-muted-foreground text-sm'>
                      <span className='text-foreground mr-2 font-medium'>
                        Description:
                      </span>
                      {profile?.description || '—'}
                    </p>
                  </div>
                </div>

                <div className='border-t p-4'>
                  <h2 className='mb-3 text-lg font-semibold'>How to Use</h2>
                  <div className='text-muted-foreground space-y-4 text-sm'>
                    <div>
                      <h3 className='text-foreground mb-1 font-medium'>
                        🔐 Sign In
                      </h3>
                      <p className='leading-relaxed'>
                        Use the embedded browser on the right to log in to the
                        target platform. Complete the authentication flow
                        including any 2FA or verification steps.
                      </p>
                    </div>

                    <div>
                      <h3 className='text-foreground mb-1 font-medium'>
                        🔄 Reload Browser
                      </h3>
                      <p className='leading-relaxed'>
                        If the page doesn't load properly or you need to
                        refresh, click the "Reload Browser" button. This will
                        reload the embedded browser without losing your
                        progress.
                      </p>
                    </div>

                    <div>
                      <h3 className='text-foreground mb-1 font-medium'>
                        🗑️ Clear & Restart
                      </h3>
                      <p className='leading-relaxed'>
                        If you need to restart the authentication process, click
                        "Clear & Restart" to discard the current session and
                        start a fresh login flow.
                      </p>
                    </div>

                    <div>
                      <h3 className='text-foreground mb-1 font-medium'>
                        ✅ Save
                      </h3>
                      <p className='leading-relaxed'>
                        After successfully logging in, click "Save". You'll be
                        asked to confirm whether you completed the login. This
                        will save your authenticated session including cookies
                        and session data for future automated testing.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize='75%'>
              <ResizablePanelGroup direction='vertical'>
                <ResizablePanel defaultSize={`${topSize}%`} minSize='10%'>
                  <div className='shrink-0 px-6 py-4'>
                    <div className='flex items-center justify-between gap-4'>
                      <DialogHeader className='min-w-0 flex-1 p-0'>
                        <DialogDescription>
                          Sign in using the embedded browser. Click "Save" when
                          authentication is complete.
                        </DialogDescription>
                      </DialogHeader>
                      <div className='flex shrink-0 flex-nowrap items-center gap-2'>
                        <Button
                          size='sm'
                          variant='secondary'
                          onClick={handleReload}
                          disabled={!sessionUrl || sessionLoading || reloading}
                        >
                          {reloading ? (
                            <Loader2 className='h-4 w-4 animate-spin' />
                          ) : (
                            <RotateCw className='h-4 w-4' />
                          )}
                          Reload Browser
                        </Button>
                        {onLogInAgain && (
                          <Button
                            variant='outline'
                            size='sm'
                            onClick={() => setRestartConfirmOpen(true)}
                            disabled={isLoggingInAgain}
                          >
                            {isLoggingInAgain && (
                              <Loader2 className='h-4 w-4 animate-spin' />
                            )}
                            Clear & Restart
                          </Button>
                        )}
                        <Button
                          size='sm'
                          variant='default'
                          onClick={() => setCloseConfirmOpen(true)}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                    <div className='mt-3 space-y-2'>
                      <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 dark:border-red-900 dark:bg-red-950/20'>
                        <div className='flex items-start gap-2'>
                          <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-status-red' />
                          <p className='text-sm font-medium text-red-900 dark:text-red-100'>
                            Warning: Do not refresh this page! Your session will
                            be lost and you'll need to start over.
                          </p>
                        </div>
                      </div>
                      {!profile?.isValid ? (
                        <div className='rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900 dark:bg-amber-950/20'>
                          <p className='text-sm font-medium text-amber-900 dark:text-amber-100'>
                            ⚠️ Session Expired - This session is no longer
                            valid. Please re-authenticate or click "Clear &
                            Restart" to start over.
                          </p>
                        </div>
                      ) : (
                        <div className='rounded-md border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-900 dark:bg-blue-950/20'>
                          <p className='text-sm font-medium text-blue-900 dark:text-blue-100'>
                            💡 Tip: Complete all authentication steps, then
                            click "Save" and confirm login completion to save
                            your session.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </ResizablePanel>
                <ResizableHandle withHandle />
                <ResizablePanel defaultSize={`${100 - topSize}%`}>
                  <div
                    ref={iframeContainerRef}
                    className='bg-muted/10 h-full w-full overflow-hidden'
                  >
                    <ClipboardEnabledIframe
                      sessionUrl={sessionUrl}
                      title='Session Viewer'
                      loading={sessionLoading}
                      loadingMessage='Preparing session...'
                      noSessionMessage='Session is not available. Save a session to enable viewer playback.'
                      externalReloadKey={reloadKey}
                      className='h-full w-full border-0 p-2'
                    />
                  </div>
                </ResizablePanel>
              </ResizablePanelGroup>
            </ResizablePanel>
          </ResizablePanelGroup>
        </DialogContent>
      </Dialog>
      <AlertDialog open={closeConfirmOpen} onOpenChange={setCloseConfirmOpen}>
        <AlertDialogContent onEscapeKeyDown={(e) => e.preventDefault()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm login status</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className='space-y-3 rounded-md border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/20'>
                <div className='text-base font-semibold text-amber-900 dark:text-amber-100'>
                  ⚠️ Important: This action will overwrite the previously stored
                  session.
                </div>
                <div className='text-amber-800 dark:text-amber-200'>
                  Did you successfully complete the login process? Your choice
                  will determine whether this identity is marked as signed in or
                  logged out for future automated testing.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={(event) => {
                event.preventDefault()
                setCloseConfirmOpen(false)
              }}
              disabled={isCloseActionPending}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogCancel
              onClick={(event) => {
                event.preventDefault()
                void handleConfirmClose(false)
              }}
              disabled={isCloseActionPending}
            >
              {isCloseActionPending ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : null}
              I'm not signed in
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault()
                void handleConfirmClose(true)
              }}
              disabled={isCloseActionPending}
            >
              {isCloseActionPending ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : null}
              I'm signed in
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={restartConfirmOpen}
        onOpenChange={(open) => {
          setRestartConfirmOpen(open)
        }}
      >
        <AlertDialogContent onEscapeKeyDown={(e) => e.preventDefault()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear & Restart Session?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className='space-y-3'>
                <div>
                  This will discard the current session and start a fresh login
                  flow. Any progress you've made in the current session will be
                  lost.
                </div>
                <div className='rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/20'>
                  <div className='text-sm font-medium text-red-900 dark:text-red-100'>
                    ⚠️ Warning: You'll need to complete the login process again
                    from the beginning.
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setRestartConfirmOpen(false)
              }}
              disabled={isLoggingInAgain}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setRestartConfirmOpen(false)
                if (onLogInAgain) {
                  onLogInAgain()
                }
              }}
              disabled={isLoggingInAgain}
              className='bg-red-600 hover:bg-red-700'
            >
              {isLoggingInAgain ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : null}
              Clear & Restart
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
